import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_ZOOM = 2.5;

function distance(a, b) {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

function midpoint(a, b) {
  return {
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2,
  };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Pinch-to-zoom client-side.
 * Transform origin in alto a sinistra + ancoraggio al punto tra le dita,
 * così lo zoom agisce sulla zona che stai guardando (non dal centro di tutto il PDF).
 */
const PinchZoom = ({
  children,
  className = '',
  hint = false,
  resetKey,
  onViewportReady,
}) => {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const stateRef = useRef({
    scale: 1,
    x: 0,
    y: 0,
    mode: null,
    startScale: 1,
    startDist: 0,
    startX: 0,
    startY: 0,
    // Punto contenuto sotto il pinch all'inizio (coordinate locali non scalate)
    pivotContentX: 0,
    pivotContentY: 0,
    startPointer: null,
    lastTapAt: 0,
    lastTapX: 0,
    lastTapY: 0,
  });

  const applyTransform = useCallback(() => {
    const el = contentRef.current;
    const s = stateRef.current;
    if (!el) return;
    el.style.transformOrigin = '0 0';
    el.style.transform = `translate3d(${s.x}px, ${s.y}px, 0) scale(${s.scale})`;
    const viewport = viewportRef.current;
    if (viewport) {
      const zoomed = s.scale > 1.01;
      viewport.style.overflowY = zoomed ? 'hidden' : 'auto';
      viewport.style.overflowX = zoomed ? 'hidden' : 'hidden';
      viewport.style.touchAction = zoomed ? 'none' : 'pan-y';
    }
  }, []);

  /** Converte scroll nativo in translate, così lo zoom parte dalla pagina visibile. */
  const bakeScrollIntoTransform = useCallback(() => {
    const viewport = viewportRef.current;
    const s = stateRef.current;
    if (!viewport || s.scale > 1.01) return;
    if (viewport.scrollTop !== 0 || viewport.scrollLeft !== 0) {
      s.x -= viewport.scrollLeft;
      s.y -= viewport.scrollTop;
      viewport.scrollTop = 0;
      viewport.scrollLeft = 0;
    }
  }, []);

  const constrain = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    const s = stateRef.current;
    if (!viewport || !content) return;
    s.scale = clamp(s.scale, MIN_SCALE, MAX_SCALE);

    if (s.scale <= 1.01) {
      // Torna allo scroll nativo: ripristina scrollTop dalla translate Y
      const y = s.y;
      s.scale = 1;
      s.x = 0;
      s.y = 0;
      applyTransform();
      if (y < 0) {
        viewport.scrollTop = -y;
      }
      return;
    }

    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    const cw = content.offsetWidth * s.scale;
    const ch = content.offsetHeight * s.scale;

    // Origin 0,0: il contenuto può solo "uscire" in alto/sinistra fino a vw-cw / vh-ch
    if (cw <= vw) {
      s.x = (vw - cw) / 2;
    } else {
      s.x = clamp(s.x, vw - cw, 0);
    }
    if (ch <= vh) {
      s.y = (vh - ch) / 2;
    } else {
      s.y = clamp(s.y, vh - ch, 0);
    }
    applyTransform();
  }, [applyTransform]);

  const zoomAroundClientPoint = useCallback((clientX, clientY, nextScale) => {
    const viewport = viewportRef.current;
    const s = stateRef.current;
    if (!viewport) return;
    bakeScrollIntoTransform();
    const rect = viewport.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    const contentX = (localX - s.x) / s.scale;
    const contentY = (localY - s.y) / s.scale;
    s.scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    s.x = localX - contentX * s.scale;
    s.y = localY - contentY * s.scale;
    constrain();
  }, [bakeScrollIntoTransform, constrain]);

  const resetZoom = useCallback(() => {
    const viewport = viewportRef.current;
    const s = stateRef.current;
    s.scale = 1;
    s.x = 0;
    s.y = 0;
    s.mode = null;
    if (viewport) {
      viewport.scrollTop = 0;
      viewport.scrollLeft = 0;
    }
    applyTransform();
  }, [applyTransform]);

  useLayoutEffect(() => {
    onViewportReady?.(viewportRef.current);
    return () => onViewportReady?.(null);
  }, [onViewportReady]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const s = stateRef.current;

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        bakeScrollIntoTransform();
        applyTransform();
        s.mode = 'pinch';
        s.startScale = s.scale;
        s.startDist = distance(e.touches[0], e.touches[1]) || 1;
        s.startX = s.x;
        s.startY = s.y;
        const mid = midpoint(e.touches[0], e.touches[1]);
        const rect = viewport.getBoundingClientRect();
        const localX = mid.x - rect.left;
        const localY = mid.y - rect.top;
        // Punto del contenuto sotto le dita (resta ancorato durante il pinch)
        s.pivotContentX = (localX - s.x) / s.scale;
        s.pivotContentY = (localY - s.y) / s.scale;
        return;
      }
      if (e.touches.length === 1 && s.scale > 1.01) {
        s.mode = 'pan';
        s.startX = s.x;
        s.startY = s.y;
        s.startPointer = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onTouchMove = (e) => {
      if (s.mode === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const dist = distance(e.touches[0], e.touches[1]) || 1;
        const mid = midpoint(e.touches[0], e.touches[1]);
        const rect = viewport.getBoundingClientRect();
        const localX = mid.x - rect.left;
        const localY = mid.y - rect.top;
        s.scale = clamp(s.startScale * (dist / s.startDist), MIN_SCALE, MAX_SCALE);
        // Tieni il punto tra le dita fermo sullo schermo
        s.x = localX - s.pivotContentX * s.scale;
        s.y = localY - s.pivotContentY * s.scale;
        constrain();
        return;
      }
      if (s.mode === 'pan' && e.touches.length === 1 && s.scale > 1.01) {
        e.preventDefault();
        const t = e.touches[0];
        s.x = s.startX + (t.clientX - s.startPointer.x);
        s.y = s.startY + (t.clientY - s.startPointer.y);
        constrain();
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length === 0) {
        if (e.changedTouches.length === 1 && s.mode !== 'pinch') {
          const t = e.changedTouches[0];
          const now = Date.now();
          const near =
            Math.abs(t.clientX - s.lastTapX) < 40 &&
            Math.abs(t.clientY - s.lastTapY) < 40;
          if (near && now - s.lastTapAt < DOUBLE_TAP_MS) {
            e.preventDefault();
            if (s.scale > 1.05) {
              // Torna a 1 ancorando lo scroll alla zona guardata
              const rect = viewport.getBoundingClientRect();
              const localY = t.clientY - rect.top;
              const contentY = (localY - s.y) / s.scale;
              s.scale = 1;
              s.x = 0;
              s.y = 0;
              applyTransform();
              viewport.scrollTop = Math.max(0, contentY - localY);
            } else {
              zoomAroundClientPoint(t.clientX, t.clientY, DOUBLE_TAP_ZOOM);
            }
            s.lastTapAt = 0;
          } else {
            s.lastTapAt = now;
            s.lastTapX = t.clientX;
            s.lastTapY = t.clientY;
          }
        }
        s.mode = null;
        constrain();
      } else if (e.touches.length === 1 && s.mode === 'pinch') {
        s.mode = s.scale > 1.01 ? 'pan' : null;
        s.startX = s.x;
        s.startY = s.y;
        s.startPointer = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.01);
      zoomAroundClientPoint(e.clientX, e.clientY, s.scale * factor);
    };

    viewport.addEventListener('touchstart', onTouchStart, { passive: false });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd, { passive: false });
    viewport.addEventListener('touchcancel', onTouchEnd, { passive: false });
    viewport.addEventListener('wheel', onWheel, { passive: false });
    applyTransform();

    return () => {
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
      viewport.removeEventListener('touchcancel', onTouchEnd);
      viewport.removeEventListener('wheel', onWheel);
    };
  }, [applyTransform, bakeScrollIntoTransform, constrain, zoomAroundClientPoint]);

  useEffect(() => {
    resetZoom();
  }, [resetKey, resetZoom]);

  return (
    <div className={`relative w-full h-full min-h-0 ${className}`}>
      <div
        ref={viewportRef}
        className="w-full h-full overflow-y-auto overflow-x-hidden overscroll-contain"
        style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
        data-testid="pinch-zoom-viewport"
      >
        <div
          ref={contentRef}
          className="will-change-transform min-h-full"
          style={{ transformOrigin: '0 0', transform: 'translate3d(0,0,0) scale(1)' }}
        >
          {children}
        </div>
      </div>
      {hint ? (
        <p className="pointer-events-none absolute bottom-2 left-0 right-0 z-10 text-center text-[10px] text-zinc-500 px-2">
          Pizzica con due dita per ingrandire · doppio tap per zoom
        </p>
      ) : null}
    </div>
  );
};

export default PinchZoom;
